import * as React from 'react';
import Modal from '../../ui/modal/Modal';
import SingleFileSelector from '../../file/SingleFileSelector';
import Select from 'react-select';
import ButtonPrimary from '../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary';
import Loading from '../../ui/loading/Loading';
import { none, some, Option } from 'ts-option';
import { EcmsImportResult } from './EcmsImportTypes';
import { api } from '../../../api/api';
import JobType from '../../../model/job/JobType';

interface Props {
  onCancel: () => void;
  onSuccess: () => void;
}

interface State {
  jobTypes: Array<{ value: string; label: string }>;
  jobTypeId: string | null;
  isUploading: boolean;
  estimatedSeconds: number | null;
  errors: Option<string[]>;
  result: EcmsImportResult | null;
}

class EcmsImportModal extends React.Component<Props, State> {
  state: State = {
    jobTypes: [],
    jobTypeId: null,
    isUploading: false,
    estimatedSeconds: null,
    errors: none,
    result: null,
  };

  componentDidMount() {
    api.instance.jobTypeApi.fetchJobTypes().then((types: JobType[]) => {
      this.setState({ jobTypes: types.map(t => ({ value: t.id, label: t.name })) });
    });
  }

  handleJobTypeChange = (option: { value: string; label: string } | null) => {
    this.setState({ jobTypeId: option ? option.value : null, errors: none });
  };

  handleUpload = (file: File, clearFiles: () => void) => {
    const { jobTypeId } = this.state;
    if (!jobTypeId) return;
    this.setState({ isUploading: true, estimatedSeconds: null, errors: none });

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = (e.target && e.target.result as string) || '';
      const rows = Math.max(0, text.split('\n').filter((l: string) => l.trim()).length - 1);
      this.setState({ estimatedSeconds: Math.ceil(rows * 0.022) });
    };
    reader.readAsText(file);

    api.instance.jobApi.importEcmsStatuses(file, jobTypeId)
      .then(result => {
        this.setState({ result, isUploading: false, estimatedSeconds: null });
        clearFiles();
      })
      .catch((e: any) => {
        const data = e && e.response && e.response.data;
        const message =
          (data && data.message) ||
          (data && data.error) ||
          'An unexpected error occurred. Please try again.';
        this.setState({ errors: some([message]), isUploading: false, estimatedSeconds: null });
      });
  };

  renderForm() {
    const { jobTypes, jobTypeId, isUploading, errors } = this.state;

    return (
      <>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Job Type</label>
          <Select
            className={"c-select-component"}
            classNamePrefix={"c-select-component"}
            options={jobTypes}
            onChange={this.handleJobTypeChange}
            placeholder="Select job type..."
          />
        </div>
        <SingleFileSelector
          onUpload={this.handleUpload}
          onCancel={this.props.onCancel}
          filesUploading={isUploading}
          errors={errors}
          accept=".xls,.xlsx,.csv"
          isUploadButtonDisabled={!jobTypeId}
          loading={false}
        />
      </>
    );
  }

  renderResult() {
    const { result } = this.state;
    if (!result) return null;
    return (
      <>
        <p style={{ fontSize: 16, marginBottom: 12 }}>
          <strong>{result.approved}</strong> approved,{' '}
          <strong>{result.invalidated}</strong> invalidated,{' '}
          <strong>{result.failed}</strong> failed (total rows: {result.total})
        </p>
        {result.errors.length > 0 && (
          <div
            style={{
              maxHeight: 300,
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 4,
              padding: 8,
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            {result.errors.map((err, i) => (
              <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                <strong>{err.custRef}</strong> - {err.reason}
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonPrimary label="Close" onClick={this.props.onSuccess} variant="burgundy" />
        </div>
      </>
    );
  }

  renderProcessing() {
    const { estimatedSeconds } = this.state;
    return (
      <div style={{ padding: '32px 0', textAlign: 'center' }}>
        <Loading isLoading={true} spinnerSize={48} />
        <p style={{ marginTop: 16, color: '#666' }}>
          Processing file, please wait…
          {estimatedSeconds != null && (
            <span> (est. ~{estimatedSeconds}s)</span>
          )}
        </p>
      </div>
    );
  }

  render() {
    const { result, isUploading } = this.state;
    return (
      <Modal
        title="Upload ECMS file"
        onCloseClicked={this.props.onCancel}
        mediumWidth
      >
        {result ? this.renderResult() : isUploading ? this.renderProcessing() : this.renderForm()}
      </Modal>
    );
  }
}

export default EcmsImportModal;
